import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateGrievanceForm } from './CreateGrievanceForm';
import { GrievanceDetails } from './GrievanceDetails';
import { Badge } from '@/components/ui/badge';
import { Grievance } from '@/types/supabase';
import { formatDistanceToNow } from 'date-fns';

export const GrievanceList = () => {
  const [grievances, setGrievances] = useState<Grievance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrievance, setSelectedGrievance] = useState<Grievance | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { currentOrganization } = useAuth();
  const { logActivity } = useActivityLogger();

  useEffect(() => {
    if (currentOrganization) {
      fetchGrievances();
    }
  }, [currentOrganization]);

  const fetchGrievances = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      
      const legalDepartmentResponse = await supabase
        .from('departments')
        .select('id')
        .eq('name', 'Legal')
        .eq('organization_id', currentOrganization.organization.id)
        .single();
      
      if (legalDepartmentResponse.error) {
        console.error('Error fetching legal department:', legalDepartmentResponse.error);
        return;
      }
      
      const legalDepartmentId = legalDepartmentResponse.data.id;
      
      const { data, error } = await supabase
        .from('grievances')
        .select('*')
        .eq('organization_id', currentOrganization.organization.id)
        .eq('department_id', legalDepartmentId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching grievances:', error);
        return;
      }

      setGrievances(data as Grievance[]);
      
      // Log activity
      await logActivity({
        action: 'view',
        resourceType: 'grievance',
        details: { department: 'Legal' }
      });
    } catch (error) {
      console.error('Error in fetchGrievances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrievanceCreated = () => {
    setIsFormOpen(false);
    fetchGrievances();
  };

  const handleViewDetails = (grievance: Grievance) => {
    setSelectedGrievance(grievance);
    setIsDetailsOpen(true);
  };

  const getStatusBadge = (status: Grievance['status']) => {
    const statusStyles = {
      open: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
      in_review: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
      resolved: 'bg-green-100 text-green-800 hover:bg-green-100',
      closed: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    };
    
    const statusLabels = {
      open: 'Open',
      in_review: 'In Review',
      resolved: 'Resolved',
      closed: 'Closed',
    };

    return (
      <Badge className={statusStyles[status]} variant="outline">
        {statusLabels[status]}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityStyles = {
      low: 'bg-green-100 text-green-800 hover:bg-green-100',
      medium: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
      high: 'bg-red-100 text-red-800 hover:bg-red-100',
    };
    
    return (
      <Badge className={priorityStyles[priority as keyof typeof priorityStyles]} variant="outline">
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  if (!currentOrganization) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Please select an organization to view grievances.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Legal Department Grievances</h2>
        
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Grievance
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Create a New Grievance</DialogTitle>
            </DialogHeader>
            <CreateGrievanceForm onGrievanceCreated={handleGrievanceCreated} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p>Loading grievances...</p>
          </CardContent>
        </Card>
      ) : grievances.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <CardDescription>No grievances found. Create a new one to get started.</CardDescription>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Grievance Tickets</CardTitle>
            <CardDescription>Manage and track all legal grievance tickets.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grievances.map((grievance) => (
                  <TableRow key={grievance.id}>
                    <TableCell className="font-medium">{grievance.title}</TableCell>
                    <TableCell>{getStatusBadge(grievance.status)}</TableCell>
                    <TableCell>{getPriorityBadge(grievance.priority)}</TableCell>
                    <TableCell>{formatDistanceToNow(new Date(grievance.created_at), { addSuffix: true })}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleViewDetails(grievance)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Grievance Details</DialogTitle>
          </DialogHeader>
          {selectedGrievance && (
            <GrievanceDetails 
              grievance={selectedGrievance} 
              onGrievanceUpdated={fetchGrievances} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
